/** D1 - the battle kernel's in-place undo journal.
 *
 * `executeBattleCommand` mutates `BattleState` in place; every write goes
 * through one of the `j*` helpers below, which first records the old value.
 * `forkBattle` is "remember where the journal is", `undoTo` is "replay the
 * recorded old values backwards". Both are O(1) / O(journal delta), which is
 * what makes AI search push/pop instead of clone/discard.
 *
 * The journal is deliberately NOT part of `BattleState`: state stays plain data
 * (I6), hashes without undo bookkeeping in it, and survives structuredClone.
 * It hangs off a module-level WeakMap keyed by the state object, so it is
 * garbage-collected with the battle and never iterated (I3).
 *
 * Entries are struct-of-arrays with a tag, so a write costs five array pushes
 * and no object allocation.
 */

import type { BattleState } from "./state.js";

const TAG_I32 = 0;
const TAG_FIELD = 1;

export interface BattleJournal {
  readonly tag: number[];
  readonly arr: (Int32Array | null)[];
  readonly obj: (Record<string, unknown> | null)[];
  readonly key: (string | number)[];
  readonly old: unknown[];
}

/** A position in the journal. Plain data so it can be stored in a search node. */
export interface JournalMark {
  readonly n: number;
}

const JOURNALS = new WeakMap<object, BattleJournal>();

export function getJournal(state: BattleState): BattleJournal {
  let j = JOURNALS.get(state);
  if (j === undefined) {
    j = { tag: [], arr: [], obj: [], key: [], old: [] };
    JOURNALS.set(state, j);
  }
  return j;
}

/** Snapshot the journal position. Pair with `undoTo` to roll a subtree back. */
export function forkBattle(state: BattleState): JournalMark {
  return { n: getJournal(state).tag.length };
}

/** Roll every write recorded after `mark` back, newest first. */
export function undoTo(state: BattleState, mark: JournalMark): void {
  const j = getJournal(state);
  for (let p = j.tag.length - 1; p >= mark.n; p--) {
    if (j.tag[p] === TAG_I32) {
      j.arr[p]![j.key[p] as number] = j.old[p] as number;
    } else {
      j.obj[p]![j.key[p] as string] = j.old[p];
    }
  }
  j.tag.length = mark.n;
  j.arr.length = mark.n;
  j.obj.length = mark.n;
  j.key.length = mark.n;
  j.old.length = mark.n;
}

/** Drop the undo history; the current state becomes the new baseline. */
export function commitBattle(state: BattleState): void {
  const j = getJournal(state);
  j.tag.length = 0; j.arr.length = 0; j.obj.length = 0; j.key.length = 0; j.old.length = 0;
}

export function journalLength(state: BattleState): number {
  return getJournal(state).tag.length;
}

// --------------------------------------------------------------- write helpers

export function jSetI32(j: BattleJournal, a: Int32Array, i: number, v: number): void {
  const prev = a[i]!;
  if (prev === v) return;
  j.tag.push(TAG_I32); j.arr.push(a); j.obj.push(null); j.key.push(i); j.old.push(prev);
  a[i] = v;
}

export function jOrI32(j: BattleJournal, a: Int32Array, i: number, bits: number): void {
  jSetI32(j, a, i, a[i]! | bits);
}

export function jAndNotI32(j: BattleJournal, a: Int32Array, i: number, bits: number): void {
  jSetI32(j, a, i, a[i]! & ~bits);
}

export function jAddI32(j: BattleJournal, a: Int32Array, i: number, delta: number): void {
  jSetI32(j, a, i, a[i]! + delta);
}

/** Record and perform a property write on any plain object or array in state. */
export function jSetField<T>(j: BattleJournal, o: object, k: string | number, v: T): void {
  const rec = o as Record<string | number, unknown>;
  const prev = rec[k];
  if (prev === v) return;
  j.tag.push(TAG_FIELD);
  j.arr.push(null);
  j.obj.push(rec as Record<string, unknown>);
  j.key.push(k);
  j.old.push(prev);
  rec[k] = v;
}
